// de landing pages: see config/localeHubs.ts for the rules and
// config/hubs/de.json for the slug of each page type (keep them in step).
//
// Volumes are Germany, monthly (OpenSEO, Oct 2026), with Google autocomplete
// (hl=de, gl=de) for the phrasing. "kostenlos" is appended to almost every
// query; the FAQ answers it honestly. Many voice-changer queries want live,
// Discord, WhatsApp or phone calls ("für anrufe", "beim telefonieren"):
// answered in the FAQ, never implied.
// Skipped on purpose: "KI Stimme(n)" 4.4k and "KI Stimmen Generator" 4.4k,
// where Google shows text-to-speech tools (HeyGen, ElevenLabs, Synthesia).
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_DE: HubCategory[] = [
  { label: "Musik", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Cartoons", filter: "cartoon" },
  { label: "Gaming", filter: "gaming" },
  { label: "K-Pop", filter: "korean" },
  { label: "Promis", filter: "public figure" },
];

const FREE_DE = {
  q: "Ist Jammable kostenlos?",
  a: "Alle Stimmen entdecken und die Beispiel-Cover anhören ist kostenlos. Für Cover, das Verändern deiner Stimme, das Klonen einer Stimme oder Text-to-Speech brauchst du einen Plan. Die aktuellen Optionen findest du auf der Preisseite.",
};

export const HUBS: Record<string, HubConfig> = {
  // "Stimmenverzerrer" 6.6k (KD 0), "Stimmverzerrer" 1.6k, "Stimme verändern"
  // 480, "Stimme ändern KI" 30. Autocomplete: "stimmenverzerrer kostenlos",
  // "stimmenverzerrer online", "stimmenverzerrer ki", "stimme verändern ki",
  // "stimmenverzerrer für anrufe", "stimme verändern beim telefonieren",
  // "wie kann ich meine stimme verändern". SERP mixes Voicemod / Play Store
  // apps with online AI voice changers (voicechanger.im, ElevenLabs, LALAL.AI).
  stimmenverzerrer: {
    slug: "stimmenverzerrer",
    group: "voice-changer",
    path: "/de/stimmenverzerrer",
    locale: "de",
    h1: "KI-Stimmenverzerrer online",
    highlight: "Stimmenverzerrer",
    title: "Stimmenverzerrer mit KI: Stimme online verändern | Jammable",
    metaDescription:
      "KI-Stimmenverzerrer online: Verwandle deine Stimme in die eines Sängers, einer Figur oder eines Promis. Über 60.000 realistische Stimmen.",
    heroSub:
      "Nimm Audio auf oder lade eine Datei hoch, wähl aus, wie du klingen willst, und hör deine veränderte Stimme in Sekunden.",
    intro:
      "Ein klassischer Stimmverzerrer macht deine Stimme nur höher, tiefer oder robotisch. Ein KI-Stimmenverzerrer verwandelt sie in eine echte andere Stimme: Sänger, Rapper, Anime- und Cartoon-Figuren oder eine Stimme, die du selbst trainiert hast.",
    appName: "Jammable KI-Stimmenverzerrer",
    steps: [
      {
        title: "Aufnehmen oder hochladen",
        body: "Nimm dich mit dem Mikrofon auf oder lade die Audiodatei hoch, die du verändern willst.",
      },
      {
        title: "Stimme wählen",
        body: "Über 60.000 KI-Stimmen, von Figuren und Cartoons bis zu Sängern und Promis.",
      },
      {
        title: "Stimme verändern",
        body: "In Sekunden hast du dein Audio mit der neuen Stimme, bereit zum Herunterladen.",
      },
    ],
    faqs: [
      {
        q: "Was ist ein KI-Stimmenverzerrer?",
        a: "Ein Tool, das eine aufgenommene Audiodatei so umwandelt, dass sie wie eine andere Stimme klingt. Anders als ein Stimmverzerrer mit Effekten klingt das Ergebnis wie die gewählte echte Stimme, nicht wie ein Filter.",
      },
      {
        q: "Wie kann ich meine Stimme mit KI verändern?",
        a: "Nimm Audio auf oder lade es hoch, wähl die Stimme, die du willst, und klick auf Erstellen. Jammable wandelt dein Audio um und gibt dir ein sauberes Ergebnis zum Herunterladen.",
      },
      {
        q: "Funktioniert das live, auf Discord oder bei Anrufen?",
        a: "Nein. Jammable wandelt aufgenommene oder hochgeladene Audiodateien um, nicht das Live-Mikrofon oder Anrufe. Dadurch klingt das Ergebnis viel realistischer als bei einem Echtzeit-Stimmenverzerrer. Die fertige Datei kannst du herunterladen und überall teilen.",
      },
      {
        q: "Brauche ich eine App oder einen Download?",
        a: "Nein. Jammable läuft online im Browser, auf dem PC genauso wie auf dem Handy.",
      },
      {
        q: "Kann ich meine Stimme in eine Figur verwandeln?",
        a: "Ja. Es gibt Tausende Stimmen von Anime-, Cartoon- und Gaming-Figuren, und jeden Tag kommen neue dazu.",
      },
      FREE_DE,
    ],
    categories: CATEGORIES_DE,
    related: [
      { label: "KI-Cover erstellen", href: "/de/ki-cover" },
      { label: "Stimme klonen", href: "/de/stimme-klonen" },
      { label: "Alle KI-Stimmen", href: "/models" },
    ],
  },

  // "ai cover" 390 (KD 21), "KI singt" 70, "KI Cover" 30. Autocomplete:
  // "ki cover erstellen", "ki cover song erstellen", "ki cover mit eigener
  // stimme", "ki singt mit meiner stimme", "ki singt meinen song",
  // "lied mit ki stimme erstellen", "ai cover generator".
  "ki-cover": {
    slug: "ki-cover",
    group: "cover",
    path: "/de/ki-cover",
    locale: "de",
    h1: "KI-Cover erstellen",
    highlight: "KI-Cover",
    title: "KI-Cover erstellen: AI Cover mit jeder Stimme | Jammable",
    metaDescription:
      "Erstelle KI-Cover mit über 60.000 Stimmen: Sänger, Rapper, Figuren und Promis. Lass die KI deinen Song in einer anderen Stimme singen, in Studioqualität.",
    heroSub:
      "Wähl eine Stimme, füg Audio hinzu, für das du die Rechte hast, und Jammable gibt dir in Sekunden ein KI-Cover zurück.",
    intro:
      "Früher brauchte man für einen Song in einer anderen Stimme ein Studio und stundenlanges Mixing. Bei Jammable wählst du die Stimme, lädst dein Audio hoch und die KI erledigt den Rest: Gesang trennen, umwandeln und neu abmischen.",
    appName: "Jammable KI-Cover-Generator",
    steps: [
      {
        title: "Stimme wählen",
        body: "Über 60.000 KI-Stimmen: Sänger, Rapper, Anime- und Cartoon-Figuren oder eine Stimme, die du selbst trainierst.",
      },
      {
        title: "Audio hinzufügen",
        body: "Lade eine Datei hoch, nimm dich beim Singen auf oder generiere einen eigenen Song mit KI.",
      },
      {
        title: "Cover erstellen",
        body: "Jammable wandelt die Stimme um und mischt das Ergebnis ab. Lade es herunter und teil es auf TikTok oder YouTube.",
      },
    ],
    faqs: [
      {
        q: "Was ist ein KI-Cover?",
        a: "Eine Version eines Songs, die mit Hilfe von KI in einer anderen Stimme gesungen wird. Du nimmst einen Track oder deine eigene Stimme und wandelst sie in die Stimme eines Künstlers, einer Figur oder einer selbst trainierten Stimme um. Melodie und Text bleiben gleich.",
      },
      {
        q: "Wie erstelle ich ein KI-Cover?",
        a: "Wähl die Stimme auf Jammable, lade das Audio hoch und klick auf Erstellen. Jammable trennt den Gesang von der Musik, wandelt ihn in die gewählte Stimme um und mischt alles in unter einer Minute neu ab. Nutze nur Audio, für das du die Rechte hast.",
      },
      {
        q: "Kann die KI mit meiner Stimme singen?",
        a: "Ja. Trainiere ein Modell deiner Stimme aus mindestens 6 Minuten sauberem Audio und nutze es dann in jedem Cover.",
      },
      {
        q: "Kann ich einen eigenen Text von der KI singen lassen?",
        a: "Ja. Beschreib einen Song oder gib deinen Text ein, und Jammable generiert einen Song, den du dann in jeder Stimme singen lassen kannst.",
      },
      FREE_DE,
      {
        q: "Wie gut klingen KI-Cover?",
        a: "Sie können sehr nah an eine echte Aufnahme herankommen. Jammable nutzt hochwertige Stimmumwandlung und automatisches Mixing; die bezahlten Pläne bieten Einstellungen für noch höhere Qualität.",
      },
    ],
    categories: CATEGORIES_DE,
    related: [
      { label: "KI-Stimmenverzerrer", href: "/de/stimmenverzerrer" },
      { label: "Stimme klonen", href: "/de/stimme-klonen" },
      { label: "Alle KI-Stimmen", href: "/models" },
    ],
  },

  // "Stimme klonen" 480 (KD 0), "Stimme klonen KI" 50. Autocomplete:
  // "stimme klonen kostenlos", "eigene stimme klonen", "stimmenklonen ki
  // deutsch", "ki stimme klonen online", "stimme klonen app".
  "stimme-klonen": {
    slug: "stimme-klonen",
    group: "clone",
    path: "/de/stimme-klonen",
    locale: "de",
    h1: "Stimme klonen mit KI",
    highlight: "klonen",
    title: "Stimme klonen mit KI: Eigenes Stimmmodell erstellen | Jammable",
    metaDescription:
      "Klone deine eigene Stimme mit KI aus 6 Minuten Audio. Nutze deine geklonte Stimme für Cover, Audios und Text-to-Speech. Eigene RVC-Modelle hochladen.",
    heroSub:
      "Lade mindestens 6 Minuten sauberes Audio hoch und Jammable trainiert ein Modell dieser Stimme für Cover und Text-to-Speech.",
    intro:
      "Eine Stimme mit KI klonen heißt, ein Modell zu trainieren, das lernt, wie sie klingt. Danach kann diese Stimme singen oder sprechen, was du willst. Klone nur deine eigene Stimme oder Stimmen, für die du die Erlaubnis hast.",
    appName: "Jammable Stimme klonen",
    steps: [
      {
        title: "Audio hochladen",
        body: "Mindestens 6 Minuten gesungene oder gesprochene Stimme, sauber und ohne lange Pausen.",
      },
      {
        title: "Modell trainieren",
        body: "Jammable trainiert die Stimme für dich. Keine Grafikkarte, kein Technikwissen nötig.",
      },
      {
        title: "Stimme nutzen",
        body: "Erstelle Cover, wandle Audios um oder tipp einen Text ein und hör ihn mit deiner geklonten Stimme.",
      },
    ],
    faqs: [
      {
        q: "Wie kann ich meine Stimme klonen?",
        a: "Lade mindestens 6 Minuten sauberes Audio der Stimme hoch, gib ihr einen Namen und Jammable trainiert das Modell. Danach erscheint die Stimme in deiner Bibliothek, bereit zur Nutzung.",
      },
      {
        q: "Wie viel Audio brauche ich, um eine Stimme zu klonen?",
        a: "Mindestens 6 Minuten saubere Stimme, ohne Hintergrundmusik und lange Pausen. Mehr Audio in besserer Qualität ergibt ein genaueres Ergebnis.",
      },
      {
        q: "Funktioniert das Klonen auch mit deutscher Stimme?",
        a: "Ja. Das Modell lernt den Klang einer Stimme, nicht die Sprache. Du kannst deine Stimme auf Deutsch, Englisch oder in jeder anderen Sprache aufnehmen und in Covern nutzen.",
      },
      {
        q: "Kann ich ein vorhandenes RVC-Modell hochladen?",
        a: "Ja. Lade die .pth- und .index-Dateien in deiner Bibliothek hoch und die Stimme ist sofort einsatzbereit.",
      },
      {
        q: "Darf ich die Stimme einer anderen Person klonen?",
        a: "Nur mit ihrer Erlaubnis. Klone deine eigene Stimme oder Stimmen, für die du die Rechte hast.",
      },
      FREE_DE,
    ],
    categories: CATEGORIES_DE,
    related: [
      { label: "Eigene Stimme trainieren", href: "/train" },
      { label: "KI-Cover erstellen", href: "/de/ki-cover" },
      { label: "KI-Stimmenverzerrer", href: "/de/stimmenverzerrer" },
    ],
  },
};
